import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ResourceUserAvailabilityGroupType } from '~/types';
import { mapScaleToPeriodResolution } from '~/modules/resourcing/common/util';
import {
  useResourceUserAvailability,
  useResourceUserAvailabilitySeriesTotals
} from '~/modules/resource-management/ResourcingOverview/components/ResourceAvailabilityCard/hooks';
import { mapToChartData } from './data-conversion';

const defaultGroupDropdownLabelKeys = {
  [ResourceUserAvailabilityGroupType.CostCenter]: 'dropdownItem.noCostCenter',
  [ResourceUserAvailabilityGroupType.Department]: 'dropdownItem.noDepartment',
  [ResourceUserAvailabilityGroupType.Division]: 'dropdownItem.noDivision',
  [ResourceUserAvailabilityGroupType.EmployeeType]:
    'dropdownItem.noEmployeeType',
  [ResourceUserAvailabilityGroupType.Location]: 'dropdownItem.noLocation',
  [ResourceUserAvailabilityGroupType.Role]: 'dropdownItem.noRole',
  [ResourceUserAvailabilityGroupType.ServiceCenter]:
    'dropdownItem.noServiceCenter'
};

export const DEFAULT_PAGE_SIZE = 5;

export const useBenchOverAllocationChartAndTableData = ({
  dateRange,
  filter,
  groupBy,
  overviewUnit,
  periodScale,
  sort,
  page
}) => {
  const { formatMessage } = useIntl();
  const {
    resourceUserAvailability,
    loadingRows,
    loadMoreRows,
    hasMoreRows
  } = useResourceUserAvailability({
    groupBy,
    dateRange,
    periodResolution: mapScaleToPeriodResolution(periodScale),
    sort,
    filter,
    pageSize: DEFAULT_PAGE_SIZE,
    page
  });

  const {
    resourceUserAvailabilitySeriesTotals,
    loading: loadingTotals
  } = useResourceUserAvailabilitySeriesTotals({
    groupBy,
    dateRange,
    periodResolution: mapScaleToPeriodResolution(periodScale),
    filter
  });

  const defaultGroupDropdownLabel = useMemo(
    () => formatMessage({ id: defaultGroupDropdownLabelKeys[groupBy] }),
    [formatMessage, groupBy]
  );

  const availabilityData = (resourceUserAvailability || []).map(
    availabilityItem =>
      mapToChartData({
        availabilityItem,
        groupBy,
        overviewUnit,
        defaultGroupDropdownLabel
      })
  );

  const availabilityTotals = resourceUserAvailabilitySeriesTotals
    ? mapToChartData({
        availabilityItem: resourceUserAvailabilitySeriesTotals,
        groupBy,
        overviewUnit,
        defaultGroupDropdownLabel
      })
    : null;

  return {
    availabilityData,
    availabilityTotals,
    loading: loadingRows || loadingTotals,
    loadMoreRows,
    hasMoreRows
  };
};
